import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Card } from "../components/ui/card";
import { Loader2, ShieldCheck, KeyRound, Building2, ShieldOff, ShieldPlus, BookOpen, Download } from "lucide-react";
import MfaCard from "../components/MfaCard";
import DomainChipInput from "../components/DomainChipInput";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import AlertBanner from "../components/AlertBanner";
import { downloadAuthenticated } from "../lib/download";

export default function SettingsPage() {
    const { user, refreshMe, canAdmin, isSuperAdmin, companyId } = useAuth();
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [changing, setChanging] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled || false);
    const [mfaUpdating, setMfaUpdating] = useState(false);
    const [company, setCompany] = useState(null);
    const [loadingCompany, setLoadingCompany] = useState(false);

    // BLOCCO 4 — domain lists state
    const [trustedDomains, setTrustedDomains] = useState([]);
    const [blockedDomains, setBlockedDomains] = useState([]);
    const [initialTrusted, setInitialTrusted] = useState([]);
    const [initialBlocked, setInitialBlocked] = useState([]);
    const [domainsMeta, setDomainsMeta] = useState({ updated_at: null });
    const [loadingDomains, setLoadingDomains] = useState(false);
    const [savingDomains, setSavingDomains] = useState(false);
    const [affectedCounts, setAffectedCounts] = useState(null);

    useEffect(() => setMfaEnabled(user?.mfa_enabled || false), [user]);

    useEffect(() => {
        if (canAdmin() && companyId && companyId !== "__all__") {
            setLoadingCompany(true);
            api.get("/company/info").then((r) => setCompany(r.data?.company || null)).catch(() => { }).finally(() => setLoadingCompany(false));
        }
    }, [companyId, canAdmin]);

    // Load trusted/blocked domains for the current company
    useEffect(() => {
        if (!companyId || companyId === "__all__") return;
        setLoadingDomains(true);
        api.get("/company/domains")
            .then((r) => {
                const t = r.data?.trusted || [];
                const b = r.data?.blocked || [];
                setTrustedDomains(t);
                setBlockedDomains(b);
                setInitialTrusted(t);
                setInitialBlocked(b);
                setDomainsMeta({ updated_at: r.data?.updated_at });
                // Kick an initial preview so counters render at 0 or actual value
                setAffectedCounts({ trusted: 0, blocked: 0 });
            })
            .catch(() => { })
            .finally(() => setLoadingDomains(false));
    }, [companyId]);

    // BLOCCO 5: debounced live preview of affected PEC counts
    useEffect(() => {
        if (!companyId || companyId === "__all__") return;
        const handle = setTimeout(() => {
            api.get("/company/domains/preview", {
                params: {
                    trusted: trustedDomains.join(","),
                    blocked: blockedDomains.join(","),
                },
            }).then((r) => {
                setAffectedCounts({
                    trusted: r.data?.trusted_count ?? 0,
                    blocked: r.data?.blocked_count ?? 0,
                });
            }).catch(() => { });
        }, 400);
        return () => clearTimeout(handle);
    }, [trustedDomains, blockedDomains, companyId]);

    const domainsDirty = (
        JSON.stringify(trustedDomains) !== JSON.stringify(initialTrusted) ||
        JSON.stringify(blockedDomains) !== JSON.stringify(initialBlocked)
    );

    const saveDomains = async () => {
        setSavingDomains(true);
        try {
            const { data } = await api.put("/company/domains", {
                trusted: trustedDomains,
                blocked: blockedDomains,
            });
            toast.success("Domini aziendali aggiornati");
            setInitialTrusted(data.trusted || []);
            setInitialBlocked(data.blocked || []);
            setDomainsMeta({ updated_at: data.updated_at });
            setAffectedCounts(data.affected_pec_counts || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore di salvataggio");
        } finally {
            setSavingDomains(false);
        }
    };

    const resetDomains = () => {
        setTrustedDomains(initialTrusted);
        setBlockedDomains(initialBlocked);
    };

    const changePassword = async (ev) => {
        ev.preventDefault();
        if (newPwd.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
        if (newPwd !== confirmPwd) return toast.error("Le password non coincidono");
        setChanging(true);
        try {
            await api.post("/auth/password/change", {
                current_password: currentPwd,
                new_password: newPwd,
            });
            toast.success("Password aggiornata");
            setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setChanging(false);
        }
    };

    const toggleMfa = async (checked) => {
        setMfaUpdating(true);
        try {
            if (checked) {
                await api.post("/auth/mfa/verify", {});
                toast.success("MFA abilitato (stub)");
            } else {
                await api.post("/auth/mfa/disable", {});
                toast.success("MFA disabilitato");
            }
            setMfaEnabled(checked);
            await refreshMe();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setMfaUpdating(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Impostazioni" subtitle="Gestisci il tuo account, la sicurezza e le informazioni aziendali." />

            <Tabs defaultValue="profile" className="w-full">
                <TabsList data-testid="settings-tabs" className="bg-white border border-[#DCE3EE]">
                    <TabsTrigger value="profile" data-testid="tab-profile">Profilo</TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security">Sicurezza</TabsTrigger>
                    <TabsTrigger value="docs" data-testid="tab-docs">Documentazione</TabsTrigger>
                    {canAdmin() && (
                        <TabsTrigger value="domains" data-testid="tab-domains">Domini fidati</TabsTrigger>
                    )}
                    {canAdmin() && (
                        <TabsTrigger value="company" data-testid="tab-company">Azienda</TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="profile" className="mt-6">
                    <Card className="p-6 border-[#DCE3EE]">
                        <h2 className="text-lg font-semibold text-[#172033] mb-4">Il tuo profilo</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                            <div>
                                <Label className="text-xs">Nome completo</Label>
                                <Input value={user?.full_name || ""} readOnly className="mt-1 h-10 bg-[#F5F7FB]" data-testid="profile-name" />
                            </div>
                            <div>
                                <Label className="text-xs">Email</Label>
                                <Input value={user?.email || ""} readOnly className="mt-1 h-10 bg-[#F5F7FB]" data-testid="profile-email" />
                            </div>
                            <div>
                                <Label className="text-xs">Ruolo globale</Label>
                                <Input value={user?.role_global === "super_admin" ? "Super Admin" : "Utente"} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                            </div>
                            <div>
                                <Label className="text-xs">Stato</Label>
                                <Input value={user?.is_active ? "Attivo" : "Disabilitato"} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="security" className="mt-6 space-y-6">
                    <Card className="p-6 border-[#DCE3EE]">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                                <KeyRound className="w-5 h-5 text-[#2563EB]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#172033]">Cambia password</h2>
                                <p className="text-xs text-[#64748B]">Almeno 8 caratteri.</p>
                            </div>
                        </div>
                        <form onSubmit={changePassword} className="space-y-4 max-w-md">
                            <div>
                                <Label htmlFor="cur">Password attuale</Label>
                                <Input id="cur" type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} className="mt-1 h-10" data-testid="current-password-input" />
                            </div>
                            <div>
                                <Label htmlFor="new">Nuova password</Label>
                                <Input id="new" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1 h-10" data-testid="new-password-input" />
                            </div>
                            <div>
                                <Label htmlFor="conf">Conferma nuova password</Label>
                                <Input id="conf" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="mt-1 h-10" data-testid="confirm-password-input" />
                            </div>
                            <Button type="submit" disabled={changing} data-testid="change-password-submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10">
                                {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna password"}
                            </Button>
                        </form>
                    </Card>

                    <MfaCard />
                </TabsContent>

                <TabsContent value="docs" className="mt-6 space-y-6" data-testid="docs-tab-content">
                    <Card className="p-6 border-[#DCE3EE]">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
                                <BookOpen className="w-6 h-6 text-[#2563EB]" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-semibold text-[#172033]">Manuale Operativo</h2>
                                <p className="text-sm text-[#64748B] mt-1 leading-relaxed">
                                    Guida completa all&apos;uso della piattaforma IH PEC Guard. Copre accesso e ruoli,
                                    setup iniziale, configurazione delle caselle IMAP dei principali gestori PEC
                                    italiani, lettura dei 5 punteggi di rischio, coda revisioni, anagrafiche,
                                    regole personalizzate, report, audit log, integrazioni AI e Web Risk,
                                    retention e troubleshooting.
                                </p>
                                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                    <div className="rounded-lg bg-[#F5F7FB] border border-[#DCE3EE] p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Versione</div>
                                        <div className="font-semibold text-[#172033] mt-0.5">1.0</div>
                                    </div>
                                    <div className="rounded-lg bg-[#F5F7FB] border border-[#DCE3EE] p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Formato</div>
                                        <div className="font-semibold text-[#172033] mt-0.5">PDF · A4</div>
                                    </div>
                                    <div className="rounded-lg bg-[#F5F7FB] border border-[#DCE3EE] p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Sezioni</div>
                                        <div className="font-semibold text-[#172033] mt-0.5">15 + copertina</div>
                                    </div>
                                    <div className="rounded-lg bg-[#F5F7FB] border border-[#DCE3EE] p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Target</div>
                                        <div className="font-semibold text-[#172033] mt-0.5">Tutti i ruoli</div>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-wrap gap-3">
                                    <Button
                                        onClick={() => downloadAuthenticated("/api/user-manual.pdf", "Manuale-IH-PEC-Guard.pdf")}
                                        data-testid="docs-download-manual-btn"
                                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Scarica manuale (PDF)
                                    </Button>
                                    <div className="text-xs text-[#64748B] self-center">
                                        Il download è protetto: viene registrato nel log di audit della tua azienda.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                {canAdmin() && (
                    <TabsContent value="domains" className="mt-6 space-y-6" data-testid="domains-tab-content">
                        {companyId === "__all__" ? (
                            <AlertBanner variant="info">
                                Sei in modalità &quot;Tutte le aziende&quot;. Seleziona un&apos;azienda specifica per configurare i domini fidati e bloccati.
                            </AlertBanner>
                        ) : (
                            <>
                                <AlertBanner variant="info">
                                    I domini configurati qui vengono applicati automaticamente alle prossime analisi PEC.
                                    Le PEC già analizzate non vengono ricalcolate. Le liste sono company-scoped.
                                </AlertBanner>

                                <Card className="p-6 border-[#DCE3EE]">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-lg bg-[#16A34A]/10 flex items-center justify-center">
                                            <ShieldPlus className="w-5 h-5 text-[#16A34A]" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-[#172033]">Domini fidati</h2>
                                            <p className="text-xs text-[#64748B]">
                                                Le PEC provenienti da questi domini ricevono un bonus di affidabilità mittente (+20).
                                            </p>
                                        </div>
                                    </div>
                                    <DomainChipInput
                                        value={trustedDomains}
                                        onChange={setTrustedDomains}
                                        variant="trusted"
                                        placeholder="Esempi: pec.gruppointernal.it, azienda-partner.it"
                                        testIdPrefix="trusted-domain"
                                    />
                                </Card>

                                <Card className="p-6 border-[#DCE3EE]">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-lg bg-[#DC2626]/10 flex items-center justify-center">
                                            <ShieldOff className="w-5 h-5 text-[#DC2626]" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-[#172033]">Domini bloccati</h2>
                                            <p className="text-xs text-[#64748B]">
                                                Le PEC provenienti da questi domini ricevono <strong>sender_reliability = 0</strong> e
                                                un aumento di rischio operativo (+50). In caso un dominio sia sia fidato che bloccato,
                                                prevale il blocco (fail-safe).
                                            </p>
                                        </div>
                                    </div>
                                    <DomainChipInput
                                        value={blockedDomains}
                                        onChange={setBlockedDomains}
                                        variant="blocked"
                                        placeholder="Esempi: phishing-hub.net, spam-domain.org"
                                        testIdPrefix="blocked-domain"
                                    />
                                </Card>

                                {affectedCounts && (
                                    <div className="text-xs text-[#64748B]" data-testid="affected-counts-info">
                                        <strong>Preview in tempo reale:</strong> PEC in archivio con un dominio fidato: <strong data-testid="affected-trusted-count">{affectedCounts.trusted}</strong>,
                                        {" "}con un dominio bloccato: <strong data-testid="affected-blocked-count">{affectedCounts.blocked}</strong>.
                                        {" "}Le modifiche vengono applicate solo alle prossime analisi.
                                    </div>
                                )}

                                <div className="flex items-center justify-between pt-2">
                                    <div className="text-[11px] text-[#64748B]">
                                        {domainsMeta.updated_at ? (
                                            <>Ultimo aggiornamento: {new Date(domainsMeta.updated_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</>
                                        ) : (
                                            <>Nessuna modifica registrata.</>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={resetDomains}
                                            disabled={!domainsDirty || savingDomains}
                                            data-testid="domains-reset-btn"
                                        >
                                            Annulla modifiche
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={saveDomains}
                                            disabled={!domainsDirty || savingDomains || loadingDomains}
                                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                                            data-testid="domains-save-btn"
                                        >
                                            {savingDomains ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva modifiche"}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </TabsContent>
                )}

                {canAdmin() && (
                    <TabsContent value="company" className="mt-6">
                        <Card className="p-6 border-[#DCE3EE]">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-[#2563EB]" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-[#172033]">Informazioni azienda</h2>
                                    <p className="text-xs text-[#64748B]">Visualizzazione dell&apos;azienda corrente.</p>
                                </div>
                            </div>
                            {isSuperAdmin() && companyId === "__all__" ? (
                                <AlertBanner variant="info">
                                    Sei in modalità &quot;Tutte le aziende&quot;. Seleziona un&apos;azienda specifica per vedere i dettagli.
                                </AlertBanner>
                            ) : loadingCompany ? (
                                <p className="text-sm text-[#64748B]">Caricamento…</p>
                            ) : company ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                                    <div>
                                        <Label className="text-xs">Nome</Label>
                                        <Input value={company.name || ""} readOnly className="mt-1 h-10 bg-[#F5F7FB]" data-testid="company-name-field" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Partita IVA</Label>
                                        <Input value={company.vat_number || ""} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label className="text-xs">Indirizzo</Label>
                                        <Input value={company.address || ""} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">AI abilitata</Label>
                                        <Input value={company.ai_enabled ? "Sì" : "No"} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Ritenzione (giorni)</Label>
                                        <Input value={String(company.retention_days || 365)} readOnly className="mt-1 h-10 bg-[#F5F7FB]" />
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-[#64748B]">Nessuna informazione azienda disponibile.</p>
                            )}
                        </Card>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
